import { useCallback, useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Computes a fixed {top, left, width} position for a popover anchored to a trigger element.
 * Default: aligns the popover's left edge to the trigger's left edge, offset 6px below.
 * Right-edge flip: when left + popoverWidth + 16px margin would overflow the viewport,
 * the popover's right edge is aligned to the trigger's right edge, clamped to 16px minimum.
 *
 * `width` mirrors the trigger element's measured width. Callers that want their popover
 * to match the trigger (e.g. a search dropdown sized to its input) apply `width: pos.width`
 * to their style; callers that want a fixed-width popover simply ignore the field.
 *
 * @param isOpen - When true (default), scroll and resize listeners keep the position
 *   up to date while the popover is visible. Pass `false` to disable tracking (e.g.
 *   when the caller conditionally mounts the popover only while open, making tracking
 *   redundant). Defaults to true so callers that omit the flag retain the original
 *   always-tracking behaviour.
 * @param popoverHeight - Caller-supplied estimate of the popover's height in pixels.
 *   When provided and the panel would overflow the viewport bottom
 *   (rect.bottom + 6 + popoverHeight > window.innerHeight - 16), the popover flips
 *   above the trigger: top = max(16, rect.top - popoverHeight - 6). Omitting this
 *   parameter preserves the legacy below-anchor default (top = rect.bottom + 6)
 *   with no vertical flip. Use a pinned constant for panels of known, bounded height.
 */
export function useAnchoredPos(
	anchorRef: RefObject<HTMLElement | null>,
	popoverWidth = 256,
	isOpen = true,
	popoverHeight?: number,
): { top: number; left: number; width: number } | null {
	const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

	const computePos = useCallback(() => {
		if (!anchorRef.current) return;
		const rect = anchorRef.current.getBoundingClientRect();
		const margin = 16;
		const viewportWidth = window.innerWidth;
		// Default: anchor popover left edge to trigger left edge.
		// If that would overflow viewport right, align popover right edge to trigger right edge.
		const left =
			rect.left + popoverWidth + margin > viewportWidth
				? Math.max(margin, rect.right - popoverWidth)
				: rect.left;
		// Vertical flip: when popoverHeight is supplied and the panel would overflow
		// the viewport bottom, position above the trigger instead.
		const top =
			popoverHeight !== undefined &&
			rect.bottom + 6 + popoverHeight > window.innerHeight - margin
				? Math.max(margin, rect.top - popoverHeight - 6)
				: rect.bottom + 6;
		// eslint-disable-next-line @eslint-react/set-state-in-effect -- layout measurement requires setState
		setPos({ top, left, width: rect.width });
	}, [anchorRef, popoverWidth, popoverHeight]);

	useLayoutEffect(() => {
		if (!isOpen) {
			// Reset on close so the next open recomputes before the portal paints.
			// Without this, a re-open after the page scrolled would render one frame
			// at the previous coordinates before computePos fires.
			// eslint-disable-next-line @eslint-react/set-state-in-effect -- reset on close
			setPos(null);
			return;
		}
		computePos();
		window.addEventListener('scroll', computePos, { capture: true, passive: true });
		window.addEventListener('resize', computePos);
		return () => {
			window.removeEventListener('scroll', computePos, { capture: true });
			window.removeEventListener('resize', computePos);
		};
	}, [isOpen, computePos]);

	return pos;
}
